/** Small colour helpers for tenant palettes. Inputs are #RRGGBB (validated by the backend). */

export interface Rgb {
  readonly r: number
  readonly g: number
  readonly b: number
}

export function hexToRgb(hex: string): Rgb | null {
  const match = /^#?([0-9a-f]{6})$/i.exec(hex.trim())
  if (match === null) return null
  const value = Number.parseInt(match[1] ?? '000000', 16)
  return { r: (value >> 16) & 255, g: (value >> 8) & 255, b: value & 255 }
}

export function rgbToHex({ r, g, b }: Rgb): string {
  const clamp = (n: number): number => Math.max(0, Math.min(255, Math.round(n)))
  return `#${[clamp(r), clamp(g), clamp(b)].map((n) => n.toString(16).padStart(2, '0')).join('')}`
}

/** WCAG relative luminance. */
export function luminance({ r, g, b }: Rgb): number {
  const channel = (c: number): number => {
    const s = c / 255
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4
  }
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b)
}

/** WCAG contrast ratio between two colours (1–21). */
export function contrastRatio(a: string, b: string): number {
  const ra = hexToRgb(a)
  const rb = hexToRgb(b)
  if (ra === null || rb === null) return 1
  const la = luminance(ra)
  const lb = luminance(rb)
  const [light, dark] = la > lb ? [la, lb] : [lb, la]
  return (light + 0.05) / (dark + 0.05)
}

const WHITE = '#ffffff'
const INK = '#14203a'

/** White or ink, whichever reads better on `background` (target ≥ 4.5:1; picks the higher). */
export function readableForeground(background: string): string {
  return contrastRatio(background, WHITE) >= contrastRatio(background, INK) ? WHITE : INK
}

/** Darken by `amount` (0–1) toward black — used for the sidebar gradient and hover states. */
export function darken(hex: string, amount: number): string {
  const rgb = hexToRgb(hex)
  if (rgb === null) return hex
  const k = 1 - Math.max(0, Math.min(1, amount))
  return rgbToHex({ r: rgb.r * k, g: rgb.g * k, b: rgb.b * k })
}

export function isHexColor(value: unknown): value is string {
  return typeof value === 'string' && /^#[0-9a-f]{6}$/i.test(value)
}
